import React, { useState } from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, CheckCircle, XCircle, Eye } from 'lucide-react';
import { MOCK_RENT_REQUESTS } from '../../utils/mockData';
import { RiskScoreBadge } from '../../components/shared/RiskScoreBadge';
import { formatCurrency } from '../../utils/calculations';
export function RiskManagement() {
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const pendingRequests = MOCK_RENT_REQUESTS.filter(r => r.status === 'pending');
  const approvedRequests = MOCK_RENT_REQUESTS.filter(r => r.status === 'approved');
  const rejectedRequests = MOCK_RENT_REQUESTS.filter(r => r.status === 'rejected');
  const getActiveRequests = () => {
    switch (activeTab) {
      case 'approved':
        return approvedRequests;
      case 'rejected':
        return rejectedRequests;
      default:
        return pendingRequests;
    }
  };
  const stats = {
    totalRequests: MOCK_RENT_REQUESTS.length,
    lowRisk: MOCK_RENT_REQUESTS.filter(r => r.tenantRiskScore.level === 'Low').length,
    mediumRisk: MOCK_RENT_REQUESTS.filter(r => r.tenantRiskScore.level === 'Medium').length,
    highRisk: MOCK_RENT_REQUESTS.filter(r => r.tenantRiskScore.level === 'High').length
  };
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-16 lg:pb-0" data-id="element-3333">
      <Sidebar data-id="element-3334" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-3335">
        <DashboardHeader data-id="element-3336" />

        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-3337">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-3338">
            <div className="mb-6 lg:mb-8" data-id="element-3339">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 truncate" data-id="element-3340">
                Risk Management
              </h1>
              <p className="text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-3341">
                Monitor and manage tenant risk assessments
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8" data-id="element-3342">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-colors" data-id="element-3343">
                <div className="flex items-center justify-between mb-2" data-id="element-3344">
                  <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-3345" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap" data-id="element-3346">
                    Total
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white break-all" data-id="element-3347">
                  {stats.totalRequests}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" data-id="element-3348">
                  Active Requests
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-colors" data-id="element-3349">
                <div className="flex items-center justify-between mb-2" data-id="element-3350">
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400 flex-shrink-0" data-id="element-3351" />
                  <span className="text-xs font-medium text-green-600 dark:text-green-400 whitespace-nowrap" data-id="element-3352">
                    Low Risk
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white break-all" data-id="element-3353">
                  {stats.lowRisk}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" data-id="element-3354">
                  {(stats.lowRisk / stats.totalRequests * 100).toFixed(0)}% of
                  total
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-colors" data-id="element-3355">
                <div className="flex items-center justify-between mb-2" data-id="element-3356">
                  <AlertTriangle className="h-8 w-8 text-yellow-600 dark:text-yellow-400 flex-shrink-0" data-id="element-3357" />
                  <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400 whitespace-nowrap" data-id="element-3358">
                    Medium Risk
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white break-all" data-id="element-3359">
                  {stats.mediumRisk}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" data-id="element-3360">
                  {(stats.mediumRisk / stats.totalRequests * 100).toFixed(0)}%
                  of total
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-colors" data-id="element-3361">
                <div className="flex items-center justify-between mb-2" data-id="element-3362">
                  <XCircle className="h-8 w-8 text-red-600 dark:text-red-400 flex-shrink-0" data-id="element-3363" />
                  <span className="text-xs font-medium text-red-600 dark:text-red-400 whitespace-nowrap" data-id="element-3364">
                    High Risk
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white break-all" data-id="element-3365">
                  {stats.highRisk}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" data-id="element-3366">
                  {(stats.highRisk / stats.totalRequests * 100).toFixed(0)}%
                  of total
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors" data-id="element-3367">
              <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto" data-id="element-3368">
                <div className="flex min-w-max" data-id="element-3369">
                  {(['pending', 'approved', 'rejected'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 px-6 py-4 text-sm font-medium capitalize transition-colors whitespace-nowrap ${activeTab === tab ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`} data-id="element-3370">
                      {tab} (
                      {tab === 'pending' ? pendingRequests.length : tab === 'approved' ? approvedRequests.length : rejectedRequests.length}
                      )
                    </button>)}
                </div>
              </div>

              {/* Request List */}
              <div className="divide-y divide-gray-100 dark:divide-gray-700" data-id="element-3371">
                {getActiveRequests().length === 0 ? <div className="p-8 text-center text-gray-500 dark:text-gray-400" data-id="element-3372">
                    <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" data-id="element-3373" />
                    <p data-id="element-3374">No {activeTab} requests found</p>
                  </div> : getActiveRequests().map((request, index) => <motion.div key={request.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-3375">
                      <div className="flex flex-col sm:flex-row items-start justify-between mb-4 gap-3" data-id="element-3376">
                        <div className="flex-1 min-w-0" data-id="element-3377">
                          <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-1 truncate" data-id="element-3378">
                            {request.tenantName}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-3379">
                            {request.propertyAddress}
                          </p>
                        </div>
                        <div className="flex-shrink-0" data-id="element-3380">
                          <RiskScoreBadge riskScore={request.tenantRiskScore} showDetails data-id="element-3381" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-id="element-3382">
                        <div data-id="element-3383">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate" data-id="element-3384">
                            Principal Amount
                          </p>
                          <p className="font-bold text-gray-900 dark:text-white break-all" data-id="element-3385">
                            {formatCurrency(request.principal)}
                          </p>
                        </div>
                        <div data-id="element-3386">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate" data-id="element-3387">
                            Monthly Income
                          </p>
                          <p className="font-bold text-gray-900 dark:text-white break-all" data-id="element-3388">
                            {formatCurrency(request.tenantMonthlyIncome)}
                          </p>
                        </div>
                        <div data-id="element-3389">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate" data-id="element-3390">
                            Rent-to-Income
                          </p>
                          <p className={`font-bold ${request.rentToIncomeRatio <= 40 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-3391">
                            {request.rentToIncomeRatio.toFixed(1)}%
                          </p>
                        </div>
                        <div data-id="element-3392">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate" data-id="element-3393">
                            Duration
                          </p>
                          <p className="font-bold text-gray-900 dark:text-white" data-id="element-3394">
                            {request.duration} Months
                          </p>
                        </div>
                      </div>

                      {activeTab === 'pending' && <div className="flex flex-col sm:flex-row gap-3" data-id="element-3395">
                          <button className="flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 transition-colors flex items-center justify-center" data-id="element-3396">
                            <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-3397" />
                            Approve
                          </button>
                          <button className="flex-1 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 transition-colors flex items-center justify-center" data-id="element-3398">
                            <XCircle className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-3399" />
                            Reject
                          </button>
                          <button className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap flex items-center justify-center" data-id="element-3400">
                            <Eye className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-3401" />
                            Review
                          </button>
                        </div>}
                    </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>;
}